import { Link } from '@tanstack/react-router'
import { Button } from '@trellis/roots/button'
import { RichTextRenderer } from '@trellis/roots/data'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@trellis/roots/sheet'
import { Skeleton } from '@trellis/roots/skeleton'
import { StatusBadge } from '@trellis/roots/status'
import { Typography } from '@trellis/roots/typography'
import { isRichTextEmpty } from '@trellis/shared'
import { ExternalLink, Eye, Mail, MapPin, Phone } from 'lucide-react'
import type { ReactNode } from 'react'

import { useClientQuery } from '@/hooks'
import { CLIENT_STATUS_LABELS, CLIENT_STATUS_STYLES } from '@/lib/client-status'

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-0.5">
      <Typography variant="label" color="muted">
        {label}
      </Typography>
      <div className="text-sm">{children}</div>
    </div>
  )
}

interface ClientPreviewTriggerProps {
  clientId: string
  onPreview: (clientId: string) => void
  className?: string
}

export function ClientPreviewTrigger({
  clientId,
  onPreview,
  className,
}: ClientPreviewTriggerProps) {
  return (
    <Button
      variant="ghost"
      size="sm"
      type="button"
      className={className}
      onClick={() => onPreview(clientId)}
    >
      <Eye className="h-3.5 w-3.5" />
      Preview client
    </Button>
  )
}

interface ClientPreviewSheetProps {
  clientId: string | null
  onOpenChange: (isOpen: boolean) => void
}

export function ClientPreviewSheet({ clientId, onOpenChange }: ClientPreviewSheetProps) {
  const clientQuery = useClientQuery(clientId ?? '')
  const client = clientQuery.data?.data ?? null
  const hasError = clientQuery.isError || (clientQuery.isFetched && !client)

  return (
    <Sheet open={clientId !== null} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[min(90vw,480px)] max-w-none overflow-y-auto sm:max-w-none"
      >
        <SheetHeader>
          <SheetTitle>{client?.name ?? 'Client'}</SheetTitle>
          <SheetDescription>
            {[client?.company, client && CLIENT_STATUS_LABELS[client.status]]
              .filter(Boolean)
              .join(' · ')}
          </SheetDescription>
        </SheetHeader>

        {clientQuery.isLoading && (
          <div className="mt-6 space-y-5 p-4">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {hasError && (
          <div className="text-destructive-text mt-6 p-4 text-sm">
            Failed to load client details.
          </div>
        )}

        {client && (
          <div className="mt-6 space-y-5 p-4">
            <SummaryRow label="Status">
              <StatusBadge
                status={client.status}
                styles={CLIENT_STATUS_STYLES}
                labels={CLIENT_STATUS_LABELS}
              />
            </SummaryRow>

            {(client.email || client.phone || client.address) && (
              <SummaryRow label="Contact info">
                <div className="text-muted-foreground space-y-1">
                  {client.email && (
                    <p className="flex items-center gap-1.5">
                      <Mail className="size-3.5 shrink-0" />
                      {client.email}
                    </p>
                  )}
                  {client.phone && (
                    <p className="flex items-center gap-1.5">
                      <Phone className="size-3.5 shrink-0" />
                      {client.phone}
                    </p>
                  )}
                  {client.address && (
                    <p className="flex items-center gap-1.5">
                      <MapPin className="size-3.5 shrink-0" />
                      {client.address}
                    </p>
                  )}
                </div>
              </SummaryRow>
            )}

            {client.contacts.length > 0 && (
              <SummaryRow label="Contacts">
                <div className="mt-1 grid gap-3">
                  {client.contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="bg-card space-y-1 rounded-xl border p-3 text-sm shadow-sm"
                    >
                      <p className="font-semibold">{contact.fullName}</p>
                      {contact.position && (
                        <p className="text-muted-foreground text-xs">
                          {contact.position}
                        </p>
                      )}
                      {contact.email && (
                        <p className="text-muted-foreground flex items-center gap-1.5">
                          <Mail className="size-3.5 shrink-0" />
                          {contact.email}
                        </p>
                      )}
                      {contact.phone && (
                        <p className="text-muted-foreground flex items-center gap-1.5">
                          <Phone className="size-3.5 shrink-0" />
                          {contact.phone}
                        </p>
                      )}
                      {!isRichTextEmpty(contact.notes) && (
                        <RichTextRenderer
                          content={contact.notes}
                          className="text-muted-foreground pt-1"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </SummaryRow>
            )}

            {!isRichTextEmpty(client.notes) && (
              <SummaryRow label="Notes">
                <div className="bg-muted/30 mt-1 rounded-xl border p-3">
                  <RichTextRenderer content={client.notes} className="text-sm" />
                </div>
              </SummaryRow>
            )}

            <Link
              to="/clients/$id"
              params={{ id: clientId ?? '' }}
              className="text-primary inline-flex items-center gap-1 text-sm underline underline-offset-2"
            >
              View full profile
              <ExternalLink className="size-3.5" />
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
